import {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import moment from 'moment'
import {CheckingModel} from '../../../models/ckecking.model'

interface Props {
  show: boolean
  /** Se ejecuta al presionar "No", la X o cerrar el modal (el checkout actual ya se realizó). */
  onClose: () => void
  /** Reserva a la que se le está dando salida actualmente (más cercana a hoy) */
  currentBooking?: CheckingModel | null
  /** Reserva anterior (la más alejada a la fecha de hoy) */
  previousBooking?: CheckingModel | null
  /** Callback del botón "Sí, realizar checkout". Recibe la fecha (YYYY-MM-DD) del input. */
  onConfirm: (fechaCheckout: string) => void
  loading?: boolean
}

// Formatos posibles que puede devolver el servicio (con u sin hora)
const DATE_FORMATS = ['DD/MM/YYYY HH:mm', 'DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601]

// Formatea una fecha al formato del input date (YYYY-MM-DD)
const toInputDate = (value?: string) => {
  if (!value) return ''
  const parsed = moment(value, DATE_FORMATS, true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

const toDisplayDate = (value?: string) => {
  if (!value) return ''
  const parsed = moment(value, DATE_FORMATS, true)
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value
}

const ModalPreviousCheckout: FC<Props> = ({
  show,
  onClose,
  currentBooking,
  previousBooking,
  onConfirm,
  loading = false,
}) => {
  // El texto muestra la fecha programada (FechaFin) de la reserva anterior
  const programmedDate = previousBooking?.FechaFin
  // El input arranca en la fecha de checkin y no permite fechas posteriores a ella
  const checkinCurrentDate = toInputDate(currentBooking?.FechaCheckIn)
  const checkinPreviousDate = toInputDate(previousBooking?.FechaCheckIn)
  const [fechaCheckout, setFechaCheckout] = useState<string>('')

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(fechaCheckout)
  }

  return (
    <Modal show={show} onHide={handleClose} centered backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex flex-column align-items-center text-center'>
          <div
            className='d-flex align-items-center justify-content-center mb-4'
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              border: '3px solid #50cd89',
              color: '#50cd89',
              fontSize: 34,
              fontWeight: 700,
            }}
          >
            <i className='bi bi-check-lg' style={{fontSize: 38, color: '#50cd89'}}></i>
          </div>

          <h5 className='fw-bolder mb-2'>
            Se ha dado salida a la reserva{' '}
            <span className='text-primary'>{currentBooking?.CodigoReserva}</span>
          </h5>

          <p className='mb-4'>
            Sin embargo, tiene una reserva anterior con nro{' '}
            <span className='text-primary fw-bold'>{previousBooking?.CodigoReserva}</span> con fecha
            de checkout programada{' '}
            <span className='text-primary fw-bold'>{toDisplayDate(programmedDate)}</span>
            {previousBooking?.Site ? (
              <>
                {' '}
                en el site <span className='fw-bold'>{previousBooking?.Site}</span>
              </>
            ) : null}
            .
          </p>
        </div>

        <div className='mb-2 text-start'>
          <label className='form-label required'>Fecha Maxima de checkout para la anterior reserva {previousBooking?.CodigoReserva}</label>
          <input
            type='date'
            className='form-control'
            value={fechaCheckout}
            max={checkinCurrentDate || undefined}
            min={checkinPreviousDate || undefined}
            onChange={(e) => setFechaCheckout(e.target.value)}
            disabled={loading}
          />
          <div className='form-text'>
            <i className='bi bi-info-circle me-1'></i>
            Esta es la fecha de checkout programada para la reserva anterior.
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className='flex-column'>
        <div className='d-flex w-100 gap-3'>
          <button
            type='button'
            className='btn btn-light-primary flex-fill'
            onClick={handleClose}
            disabled={loading}
          >
            No
          </button>
          <button
            type='button'
            className='btn btn-primary flex-fill'
            onClick={handleConfirm}
            disabled={loading || !fechaCheckout}
          >
            {!loading && <span className='indicator-label'>Sí, realizar checkout</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Procesando...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        <div className='alert alert-info d-flex align-items-center w-100 mt-3 mb-0'>
          <i className='bi bi-info-circle me-2'></i>
          <span className='fs-8'>
            Si presiona "Sí", la reserva anterior se realizará su checkout con la fecha indicada, la
            reserva actual se mantendrá con la fecha de checkout actual.
          </span>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export {ModalPreviousCheckout}
